import { useEffect, useState, useMemo, useCallback } from "react";
import axiosInstance from "config/axiosInstance";
import Grid from "@mui/material/Grid";
import { Card, LinearProgress, TextField, Select, MenuItem } from "@mui/material";
import { getCurrentUser, isSecondaryUser } from "utils/auth";
import NotificationSystem from "components/NotificationSystem";
import { useAppAlert } from "hooks/useAppAlert";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import TrendsChart from "components/TrendsChart";
import WeeklyPrediction from "components/WeeklyPrediction";

import { formatDateTimePeru, toPeruDateInputValue } from "utils/datePeru";
import { IoGlobe, IoWallet, IoSearchSharp, IoStatsChart, IoList } from "react-icons/io5";
import BarChart from "examples/Charts/BarCharts/BarChart";
import YapeLogo from "assets/images/logos/yape.svg";
import PlinLogo from "assets/images/logos/plin.svg";

function Dashboard() {
  const { showAlert, AlertBanner } = useAppAlert();
  const [loading, setLoading] = useState(true);
  const [totalHoy, setTotalHoy] = useState(0);
  const [totalMes, setTotalMes] = useState(0);
  const [ultimos, setUltimos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [userType, setUserType] = useState(null);

  // Format currency with thousand separators
  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    return num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatBancoName = (dominio) => {
    switch (dominio) {
      case "com.bcp.innovacxion.yapeapp":
        return "Yape (BCP)";
      case "pe.com.interbank.mobilebanking":
        return "Plin (Interbank)";
      case "com.bbva.nxt_peru":
        return "BBVA";
      case "pe.com.scotiabank.blpm.android":
        return "Scotiabank";
      case "com.scotiabank.pe.superdigital.atn":
        return "Tunki (Scotiabank)";
      default:
        return dominio.split('.').pop() || dominio;
    }
  };
  
  // Estados para el filtro de fechas
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState(() => toPeruDateInputValue(new Date()));
  const [depositosFiltrados, setDepositosFiltrados] = useState([]);
  const [isFiltrado, setIsFiltrado] = useState(false);
  const [nombreFiltro, setNombreFiltro] = useState("");
  const [bancoFiltro, setBancoFiltro] = useState(""); // Estado para filtro de banco



  // Totals computed from filtered deposits (when a filter is active)
  const filteredTotals = useMemo(() => {
    let depositos = depositosFiltrados || [];
    
    // Aplicar filtro de banco si está activo
    if (bancoFiltro) {
      depositos = depositos.filter(d => d.dominio === bancoFiltro);
    }
    
    if (depositos.length === 0) {
      return {
        count: 0,
        sum: 0,
        yape: 0,
        plin: 0,
        plinBbva: 0,
      };
    }

    let sum = 0;
    let yape = 0;
    let plin = 0;
    let plinBbva = 0;

    for (const d of depositos) {
      const monto = parseFloat(d.monto || 0);
      sum += isNaN(monto) ? 0 : monto;
      if (d.dominio === "com.bcp.innovacxion.yapeapp") yape += monto;
      if (d.dominio === "pe.com.interbank.mobilebanking") plin += monto;
      if (d.dominio === "com.bbva.nxt_peru") plinBbva += monto;
    }

    return {
      count: depositos.length,
      sum: Number(sum.toFixed(2)),
      yape: Number(yape.toFixed(2)),
      plin: Number(plin.toFixed(2)),
      plinBbva: Number(plinBbva.toFixed(2)),
    };
  }, [depositosFiltrados, bancoFiltro]);

  // Depositos después de aplicar filtro de banco (para mostrar en la tabla)
  const depositosParaMostrar = useMemo(() => {
    if (!bancoFiltro) return depositosFiltrados;
    return depositosFiltrados.filter(d => d.dominio === bancoFiltro);
  }, [depositosFiltrados, bancoFiltro]);

  // Totals computed from all deposits (ultimos)
  const totalDepositos = useMemo(() => {
    return ultimos.length;
  }, [ultimos]);

  const totalGeneral = useMemo(() => {
    return ultimos.reduce((sum, d) => sum + parseFloat(d.monto || 0), 0).toFixed(2);
  }, [ultimos]);

  const yapeTotal = useMemo(() => {
    return ultimos
      .filter(d => d.dominio === "com.bcp.innovacxion.yapeapp")
      .reduce((sum, d) => sum + parseFloat(d.monto || 0), 0)
      .toFixed(2);
  }, [ultimos]);

  const plinInterbankTotal = useMemo(() => {
    return ultimos
      .filter(d => d.dominio === "pe.com.interbank.mobilebanking")
      .reduce((sum, d) => sum + parseFloat(d.monto || 0), 0)
      .toFixed(2);
  }, [ultimos]);

  const plinBbvaTotal = useMemo(() => {
    return ultimos
      .filter(d => d.dominio === "com.bbva.nxt_peru")
      .reduce((sum, d) => sum + parseFloat(d.monto || 0), 0)
      .toFixed(2);
  }, [ultimos]);

  // Check if we have deposits for each bank
  const hasYape = useMemo(() => {
    return ultimos.some(d => d.dominio === "com.bcp.innovacxion.yapeapp");
  }, [ultimos]);

  const hasPlinInterbank = useMemo(() => {
    return ultimos.some(d => d.dominio === "pe.com.interbank.mobilebanking");
  }, [ultimos]);

  const hasPlinBbva = useMemo(() => {
    return ultimos.some(d => d.dominio === "com.bbva.nxt_peru");
  }, [ultimos]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/dashboard");

      if (res.data?.success) {
        setTotalHoy(res.data.totalHoy ?? 0);
        setTotalMes(res.data.totalMes ?? 0);
        setUltimos(res.data.ultimos ?? []);
        if (!isFiltrado) {
          setDepositosFiltrados(res.data.ultimos ?? []);
        }
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  }, [isFiltrado]);

  useEffect(() => {
    const user = getCurrentUser();
    if (user && user.tipo) {
      setUserType(user.tipo);
    }
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    // Fetch sucursales only for business users
    if (userType === "negocio") {
      const fetchSucursales = async () => {
        try {
          const res = await axiosInstance.get("/sucursales");
          if (res.data?.success) {
            setSucursales(res.data.sucursales || []);
          }
        } catch (err) {
        }
      };
      fetchSucursales();
    }
  }, [userType]);

  const handleFiltrar = async () => {
    if (!fechaInicio) {
      showAlert("Por favor selecciona la fecha de inicio", "warning");
      return;
    }

    setLoading(true);
    
    try {
      // Llamar al backend con los parámetros de fecha
      const params = { fechaInicio, fechaFin, filtrar: true };
      if (nombreFiltro && nombreFiltro.trim() !== "") params.nombre = nombreFiltro.trim();

      const res = await axiosInstance.get("/dashboard", { params });

      
      if (res.data?.success) {
        const depositos = res.data.depositosFiltrados || res.data.ultimos || [];
        setDepositosFiltrados(depositos);
        setIsFiltrado(true);
        
        if (depositos.length === 0) {
          showAlert("No se encontraron depósitos en el rango de fechas seleccionado", "warning");
        }
      } else {
        showAlert("Error al filtrar depósitos", "error");
      }
    } catch (err) {
      showAlert("Error al filtrar depósitos: " + (err.message || "Error desconocido"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiarFiltro = () => {
    setFechaInicio("");
    setFechaFin(toPeruDateInputValue(new Date()));
    setDepositosFiltrados(ultimos);
    setNombreFiltro("");
    setBancoFiltro(""); // Limpiar filtro de banco
    setIsFiltrado(false);
  };

  const formatearFecha = (fecha) => formatDateTimePeru(fecha);

  const handleAsignarSucursal = async (depositoId, sucursalId) => {
    try {
      const sucursalNombre = sucursalId 
        ? sucursales.find(s => s.id === parseInt(sucursalId))?.nombre_sucursal || "sucursal"
        : "Sin asignar";

      await axiosInstance.post(
        "/sucursales/assign-deposit",
        { depositoId: depositoId, sucursalId: sucursalId || null }
      );

      // Update local state with nombre_sucursal
      setDepositosFiltrados((prev) =>
        prev.map((d) => (d.id === depositoId ? { ...d, tienda_id: sucursalId || null, nombre_sucursal: sucursalNombre === "Sin asignar" ? null : sucursalNombre } : d))
      );
      setUltimos((prev) =>
        prev.map((d) => (d.id === depositoId ? { ...d, tienda_id: sucursalId || null, nombre_sucursal: sucursalNombre === "Sin asignar" ? null : sucursalNombre } : d))
      );

      const mensaje = sucursalId
        ? `Depósito asignado a: ${sucursalNombre}`
        : "Depósito sin asignar";
      showAlert(mensaje, "success");
    } catch (err) {
      showAlert(err.response?.data?.message || "Error al asignar sucursal", "error");
    }
  };

  // Datos para el gráfico de barras (depósitos por día) - depende de depositosParaMostrar
  const barChart = useMemo(() => {
    if (!depositosParaMostrar || depositosParaMostrar.length === 0) {
      return { series: [], options: {} };
    }

    const grouping = {};
    for (const d of depositosParaMostrar) {
      // Agrupar por fecha (YYYY-MM-DD) - extraer directamente del string sin conversión de zona horaria
      const key = d.fecha.split(" ")[0]; // Obtiene "YYYY-MM-DD" directamente
      if (!grouping[key]) grouping[key] = { count: 0, sum: 0 };
      grouping[key].count += 1;
      grouping[key].sum += parseFloat(d.monto || 0) || 0;
    }

    const labels = Object.keys(grouping).sort();
  const sums = labels.map((l) => Number(grouping[l].sum.toFixed(2)));

    // Series will show amounts (S/.) per day so the Y axis reflects monetary values
    const series = [
      {
        name: "Monto (S/.)",
        data: sums,
      },
    ];

    const options = {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 6, columnWidth: "60%" } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: labels.map((l) => {
          // l ya es "YYYY-MM-DD", extraer día y mes directamente
          const parts = l.split("-");
          const day = parts[2];
          const month = parts[1];
          return `${day}/${month}`;
        }),
        labels: { style: { colors: "#cbd5e1" } },
      },
      yaxis: {
        labels: {
          formatter: function (val) {
            return `S/. ${Number(val || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          },
          style: { colors: "#cbd5e1" },
        },
        min: 0,
      },
      colors: ["#00c853"],
      tooltip: {
        y: {
          formatter: function (val) {
            return `S/. ${Number(val || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          },
        },
      },
    };

    return { series, options };
  }, [depositosParaMostrar]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {!isSecondaryUser() && (
        <NotificationSystem onNewDeposit={fetchDashboard} />
      )}

      <VuiBox py={3}>
        <AlertBanner />
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Ingresos de hoy" }}
                count={`S/. ${formatCurrency(totalHoy)}`}
                icon={{
                  color: "info",
                  component: <IoWallet size="22px" color="white" />,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Ingresos del mes" }}
                count={`S/. ${formatCurrency(totalMes)}`}
                icon={{
                  color: "info",
                  component: <IoGlobe size="22px" color="white" />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total depósitos" }}
                count={isFiltrado ? filteredTotals.count : totalDepositos}
                icon={{ color: "info", component: <IoList size="22px" color="white" /> }}
              />
            </Grid>

            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total general" }}
                count={isFiltrado ? `S/. ${formatCurrency(filteredTotals.sum)}` : `S/. ${formatCurrency(totalGeneral)}`}
                icon={{ color: "info", component: <IoStatsChart size="22px" color="white" /> }}
              />
            </Grid>

            {hasYape && (
              <Grid item xs={12} md={6} xl={3}>
                <MiniStatisticsCard
                  title={{ text: "Yape" }}
                  count={isFiltrado ? `S/. ${formatCurrency(filteredTotals.yape)}` : `S/. ${formatCurrency(yapeTotal)}`}
                  icon={{ 
                    color: "info", 
                    component: (
                      <VuiBox
                        sx={{
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#4B1F7A",
                          borderRadius: "8px",
                          p: 0.5,
                        }}
                      >
                        <img src={YapeLogo} alt="Yape" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      </VuiBox>
                    )
                  }}
                />
              </Grid>
            )}

            {hasPlinInterbank && (
              <Grid item xs={12} md={6} xl={3}>
                <MiniStatisticsCard
                  title={{ text: "Plin Interbank" }}
                  count={isFiltrado ? `S/. ${formatCurrency(filteredTotals.plin)}` : `S/. ${formatCurrency(plinInterbankTotal)}`}
                  icon={{ 
                    color: "info", 
                    component: (
                      <VuiBox
                        sx={{
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#00A651",
                          borderRadius: "8px",
                          p: 0.5,
                        }}
                      >
                        <img src={PlinLogo} alt="Plin" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      </VuiBox>
                    )
                  }}
                />
              </Grid>
            )}

            {hasPlinBbva && (
              <Grid item xs={12} md={6} xl={3}>
                <MiniStatisticsCard
                  title={{ text: "Plin BBVA" }}
                  count={isFiltrado ? `S/. ${formatCurrency(filteredTotals.plinBbva)}` : `S/. ${formatCurrency(plinBbvaTotal)}`}
                  icon={{ 
                    color: "info", 
                    component: (
                      <VuiBox
                        sx={{
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#004B93",
                          borderRadius: "8px",
                          p: 0.5,
                        }}
                      >
                        <img src={PlinLogo} alt="Plin BBVA" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      </VuiBox>
                    )
                  }}
                />
              </Grid>
            )}
          </Grid>

          {/* Analytics Section - Trends and Prediction */}
          <Grid container spacing={3} mt={2} mb={3}>
            <Grid item xs={12} lg={8}>
              <TrendsChart />
            </Grid>
            <Grid item xs={12} lg={4}>
              <WeeklyPrediction />
            </Grid>
          </Grid>

          {/* Últimos depósitos */}
          <VuiBox mt={4}>
            <Card
              sx={{
                borderRadius: "16px",
              }}
            >
              <VuiBox p={3}>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <VuiTypography variant="h4" color="white" fontWeight="bold">
                    📋 Últimos depósitos
                  </VuiTypography>
                  {depositosParaMostrar.length > 0 && (
                    <VuiBox
                      sx={{
                        backgroundColor: "rgba(0, 200, 83, 0.15)",
                        borderRadius: "8px",
                        px: 2,
                        py: 1,
                        border: "1px solid rgba(0, 200, 83, 0.3)",
                      }}
                    >
                      <VuiTypography color="success" fontSize="0.9rem" fontWeight="bold">
                        {depositosParaMostrar.length} {depositosParaMostrar.length === 1 ? 'depósito encontrado' : 'depósitos encontrados'}
                      </VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>

                {/* Filtros de fecha */}
                <VuiBox
                  display="flex"
                  gap={2}
                  mb={3}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "12px",
                    p: 2.5,
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <VuiBox flex={1} minWidth="180px">
                    <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
                      Nombre (opcional)
                    </VuiTypography>
                    <TextField
                      type="text"
                      value={nombreFiltro}
                      onChange={(e) => setNombreFiltro(e.target.value)}
                      placeholder="Buscar por nombre..."
                      fullWidth
                      sx={{
                        "& .MuiInputBase-root": {
                          color: "white",
                          backgroundColor: "rgba(255, 255, 255, 0.06)",
                          borderRadius: "10px",
                          height: "45px",
                          fontSize: "0.95rem",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255, 255, 255, 0.12)",
                        },
                      }}
                    />
                  </VuiBox>
                  <VuiBox flex={1} minWidth="220px">
                    <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
                      Fecha inicio
                    </VuiTypography>
                    <TextField
                      type="date"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          color: "white",
                          backgroundColor: "#1a1d21",
                          borderRadius: "10px",
                          height: "48px",
                          fontSize: "0.95rem",
                          cursor: "pointer",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255, 255, 255, 0.3)",
                        },
                        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 200, 83, 0.6)",
                        },
                        "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#00c853",
                          borderWidth: "2px",
                        },
                        "& input[type='date']::-webkit-calendar-picker-indicator": {
                          filter: "invert(1) brightness(1.5)",
                          cursor: "pointer",
                          width: "20px",
                          height: "20px",
                        },
                        "& input[type='date']": {
                          colorScheme: "dark",
                          cursor: "pointer",
                        },
                        "& .MuiInputBase-input": {
                          cursor: "pointer",
                        },
                      }}
                    />
                  </VuiBox>

                  <VuiBox flex={1} minWidth="220px">
                    <VuiTypography color="text" fontSize="0.75rem" mb={0.5}>
                      Fecha fin (Hoy: {new Date().toLocaleDateString('es-PE')})
                    </VuiTypography>
                    <TextField
                      type="date"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          color: "white",
                          backgroundColor: "#1a1d21",
                          borderRadius: "10px",
                          height: "48px",
                          fontSize: "0.95rem",
                          cursor: "pointer",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255, 255, 255, 0.3)",
                        },
                        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 200, 83, 0.6)",
                        },
                        "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#00c853",
                          borderWidth: "2px",
                        },
                        "& input[type='date']::-webkit-calendar-picker-indicator": {
                          filter: "invert(1) brightness(1.5)",
                          cursor: "pointer",
                          width: "20px",
                          height: "20px",
                        },
                        "& input[type='date']": {
                          colorScheme: "dark",
                          cursor: "pointer",
                        },
                        "& .MuiInputBase-input": {
                          cursor: "pointer",
                        },
                      }}
                    />
                  </VuiBox>

                  <VuiBox display="flex" gap={2} mt={{ xs: 0, sm: 2 }}>
                    <VuiButton
                      color="info"
                      onClick={handleFiltrar}
                      sx={{
                        background: "linear-gradient(135deg, #00c853 0%, #00a843 100%)",
                        boxShadow: "0 4px 12px rgba(0, 200, 83, 0.4)",
                        minWidth: "120px",
                        height: "45px",
                        fontSize: "0.95rem",
                        "&:hover": {
                          boxShadow: "0 6px 16px rgba(0, 200, 83, 0.6)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <IoSearchSharp size="18px" style={{ marginRight: "8px" }} />
                      Buscar
                    </VuiButton>
                    <VuiButton
                      color="secondary"
                      onClick={handleLimpiarFiltro}
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        minWidth: "120px",
                        height: "45px",
                        fontSize: "0.95rem",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.15)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      Limpiar
                    </VuiButton>
                  </VuiBox>
                </VuiBox>

                {/* Botones de filtro por banco */}
                <VuiBox
                  display="flex"
                  gap={2}
                  mb={3}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "12px",
                    p: 2.5,
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <VuiTypography color="white" fontSize="0.9rem" fontWeight="bold">
                    Filtrar por banco:
                  </VuiTypography>
                  
                  <VuiButton
                    color={bancoFiltro === "" ? "info" : "secondary"}
                    onClick={() => setBancoFiltro("")}
                    sx={{
                      minWidth: "100px",
                      height: "40px",
                      fontSize: "0.85rem",
                      background: bancoFiltro === "" 
                        ? "linear-gradient(135deg, #0075FF 0%, #0056CC 100%)"
                        : "rgba(255, 255, 255, 0.1)",
                      fontWeight: bancoFiltro === "" ? "bold" : "normal",
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    Todos
                  </VuiButton>

                  <VuiButton
                    color={bancoFiltro === "com.bcp.innovacxion.yapeapp" ? "success" : "secondary"}
                    onClick={() => setBancoFiltro("com.bcp.innovacxion.yapeapp")}
                    sx={{
                      minWidth: "120px",
                      height: "40px",
                      fontSize: "0.85rem",
                      background: bancoFiltro === "com.bcp.innovacxion.yapeapp"
                        ? "linear-gradient(135deg, #722D90 0%, #5A1E73 100%)"
                        : "rgba(255, 255, 255, 0.1)",
                      fontWeight: bancoFiltro === "com.bcp.innovacxion.yapeapp" ? "bold" : "normal",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <img src={YapeLogo} alt="Yape" style={{ width: "20px", height: "20px" }} />
                    Yape
                  </VuiButton>

                  <VuiButton
                    color={bancoFiltro === "pe.com.interbank.mobilebanking" ? "warning" : "secondary"}
                    onClick={() => setBancoFiltro("pe.com.interbank.mobilebanking")}
                    sx={{
                      minWidth: "150px",
                      height: "40px",
                      fontSize: "0.85rem",
                      background: bancoFiltro === "pe.com.interbank.mobilebanking"
                        ? "linear-gradient(135deg, #00D9A5 0%, #00B88A 100%)"
                        : "rgba(255, 255, 255, 0.1)",
                      fontWeight: bancoFiltro === "pe.com.interbank.mobilebanking" ? "bold" : "normal",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <img src={PlinLogo} alt="Plin" style={{ width: "20px", height: "20px" }} />
                    Plin Interbank
                  </VuiButton>

                  <VuiButton
                    color={bancoFiltro === "com.bbva.nxt_peru" ? "info" : "secondary"}
                    onClick={() => setBancoFiltro("com.bbva.nxt_peru")}
                    sx={{
                      minWidth: "130px",
                      height: "40px",
                      fontSize: "0.85rem",
                      background: bancoFiltro === "com.bbva.nxt_peru"
                        ? "linear-gradient(135deg, #004B93 0%, #003d7a 100%)"
                        : "rgba(255, 255, 255, 0.1)",
                      fontWeight: bancoFiltro === "com.bbva.nxt_peru" ? "bold" : "normal",
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    Plin BBVA
                  </VuiButton>

                  {bancoFiltro && (
                    <VuiBox
                      sx={{
                        backgroundColor: "rgba(0, 200, 83, 0.15)",
                        borderRadius: "8px",
                        px: 2,
                        py: 1,
                        border: "1px solid rgba(0, 200, 83, 0.3)",
                        ml: "auto",
                      }}
                    >
                      <VuiTypography color="success" fontSize="0.85rem" fontWeight="bold">
                        {filteredTotals.count} depósito{filteredTotals.count !== 1 ? 's' : ''} • S/. {formatCurrency(filteredTotals.sum)}
                      </VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>

                {/* Gráfico de barras: depósitos por día (visible sólo después de Buscar) */}
                {isFiltrado && (
                  <VuiBox mt={3} mb={2}>
                    <Card
                      sx={{
                        borderRadius: "16px",
                        p: 3,
                        minHeight: "260px",
                      }}
                    >
                      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <VuiTypography variant="h5" color="white" fontWeight="bold">
                          📈 Depósitos por día
                        </VuiTypography>
                        <VuiTypography color="text" fontSize="0.85rem">
                          {depositosFiltrados.length} depósitos en rango
                        </VuiTypography>
                      </VuiBox>

                      {Array.isArray(barChart.series) && barChart.series.length > 0 ? (
                        <VuiBox sx={{ width: "100%", height: 220 }}>
                          <BarChart barChartData={barChart.series} barChartOptions={{ ...barChart.options, colors: ["#6a0dad"] }} />
                        </VuiBox>
                      ) : (
                        <VuiBox textAlign="center" py={4}>
                          <VuiTypography color="text">No hay datos para mostrar.</VuiTypography>
                        </VuiBox>
                      )}
                    </Card>
                  </VuiBox>
                )}

                {/* Tabla de depósitos */}
                {loading ? (
                  <LinearProgress />
                ) : depositosFiltrados.length > 0 ? (
                  <VuiBox
                    sx={{
                      overflowX: "auto",
                      maxHeight: "500px",
                      overflowY: "auto",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                        height: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "rgba(255, 255, 255, 0.05)",
                        borderRadius: "10px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "10px",
                        "&:hover": {
                          background: "rgba(255, 255, 255, 0.3)",
                        },
                      },
                    }}
                  >
                    {/* Header de la tabla */}
                    <VuiBox
                      display="flex"
                      sx={{
                        backgroundColor: "rgba(106, 13, 173, 0.2)",
                        borderRadius: "10px",
                        p: 2,
                        mb: 1,
                        border: "1px solid rgba(106, 13, 173, 0.4)",
                        minWidth: userType === "negocio" ? "1100px" : "950px",
                      }}
                    >
                      <VuiTypography
                        color="white"
                        fontWeight="bold"
                        fontSize="0.9rem"
                        sx={{ width: "5%", textAlign: "center" }}
                      >
                        #
                      </VuiTypography>
                      <VuiTypography
                        color="white"
                        fontWeight="bold"
                        fontSize="0.9rem"
                        sx={{ width: userType === "negocio" ? "15%" : "20%" }}
                      >
                        Fecha
                      </VuiTypography>
                      <VuiTypography
                        color="white"
                        fontWeight="bold"
                        fontSize="0.9rem"
                        sx={{ width: userType === "negocio" ? "25%" : "30%" }}
                      >
                        Mensaje
                      </VuiTypography>
                      <VuiTypography
                        color="white"
                        fontWeight="bold"
                        fontSize="0.9rem"
                        sx={{ width: userType === "negocio" ? "22%" : "27%" }}
                      >
                        Nombre
                      </VuiTypography>
                      <VuiTypography
                        color="white"
                        fontWeight="bold"
                        fontSize="0.9rem"
                        sx={{ width: userType === "negocio" ? "13%" : "18%" }}
                      >
                        Banco
                      </VuiTypography>
                      {userType === "negocio" && (
                        <VuiTypography
                          color="white"
                          fontWeight="bold"
                          fontSize="0.9rem"
                          sx={{ width: "20%", textAlign: "center" }}
                        >
                          Sucursal
                        </VuiTypography>
                      )}
                      <VuiTypography
                        color="white"
                        fontWeight="bold"
                        fontSize="0.9rem"
                        sx={{ width: "15%", textAlign: "right" }}
                      >
                        Monto
                      </VuiTypography>
                    </VuiBox>

                    {/* Filas de depósitos */}
                    {depositosParaMostrar.map((deposito, index) => (
                      <VuiBox
                        key={deposito.id}
                        display="flex"
                        alignItems="center"
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.03)",
                          borderRadius: "8px",
                          p: 2,
                          mb: 1,
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          minWidth: userType === "negocio" ? "1100px" : "950px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.06)",
                            borderColor: "rgba(0, 200, 83, 0.3)",
                            transform: "translateX(5px)",
                          },
                        }}
                      >
                        <VuiTypography
                          color="text"
                          fontSize="0.9rem"
                          sx={{ width: "5%", textAlign: "center" }}
                        >
                          {index + 1}
                        </VuiTypography>
                        <VuiTypography color="text" fontSize="0.85rem" sx={{ width: userType === "negocio" ? "15%" : "20%" }}>
                          {formatearFecha(deposito.fecha)}
                        </VuiTypography>
                        <VuiTypography
                          color="white"
                          fontSize="0.85rem"
                          sx={{
                            width: userType === "negocio" ? "25%" : "30%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {deposito.mensaje || "-"}
                        </VuiTypography>
                        <VuiTypography color="white" fontSize="0.85rem" sx={{ width: userType === "negocio" ? "22%" : "27%" }}>
                          {deposito.nombre || "-"}
                        </VuiTypography>
                        <VuiTypography color="white" fontSize="0.85rem" sx={{ width: userType === "negocio" ? "13%" : "18%" }}>
                          {formatBancoName(deposito.dominio)}
                        </VuiTypography>
                        {userType === "negocio" && (
                          <VuiBox sx={{ width: "20%", px: 1 }}>
                            <Select
                              value={deposito.tienda_id ? String(deposito.tienda_id) : ""}
                              onChange={(e) => handleAsignarSucursal(deposito.id, e.target.value)}
                              displayEmpty
                              size="small"
                              renderValue={(selected) => {
                                if (!selected) {
                                  return <em style={{ color: "rgba(255, 255, 255, 0.5)" }}>Sin asignar</em>;
                                }
                                return deposito.nombre_sucursal || "Cargando...";
                              }}
                              sx={{
                                width: "100%",
                                minHeight: "42px",
                                cursor: "pointer",
                                color: deposito.tienda_id ? "#00c853" : "white",
                                backgroundColor: deposito.tienda_id 
                                  ? "rgba(0, 200, 83, 0.15)" 
                                  : "rgba(255, 255, 255, 0.08)",
                                borderRadius: "8px",
                                fontSize: "0.85rem",
                                fontWeight: deposito.tienda_id ? "bold" : "normal",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: deposito.tienda_id 
                                    ? "rgba(0, 200, 83, 0.5)" 
                                    : "rgba(255, 255, 255, 0.2)",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "rgba(0, 200, 83, 0.7)",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#00c853",
                                },
                                "& .MuiSelect-select": {
                                  cursor: "pointer",
                                  paddingTop: "10px",
                                  paddingBottom: "10px",
                                },
                                "& .MuiSelect-icon": {
                                  color: deposito.tienda_id 
                                    ? "#00c853" 
                                    : "rgba(255, 255, 255, 0.7)",
                                  cursor: "pointer",
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    backgroundColor: "#1a1d21",
                                    border: "1px solid rgba(255, 255, 255, 0.1)",
                                    "& .MuiMenuItem-root": {
                                      color: "white",
                                      fontSize: "0.85rem",
                                      "&:hover": {
                                        backgroundColor: "rgba(0, 200, 83, 0.1)",
                                      },
                                      "&.Mui-selected": {
                                        backgroundColor: "rgba(0, 200, 83, 0.2)",
                                        "&:hover": {
                                          backgroundColor: "rgba(0, 200, 83, 0.3)",
                                        },
                                      },
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>Sin asignar</em>
                              </MenuItem>
                              {sucursales.map((s) => (
                                <MenuItem key={s.id} value={String(s.id)}>
                                  {s.nombre_sucursal}
                                </MenuItem>
                              ))}
                            </Select>
                          </VuiBox>
                        )}
                        <VuiTypography
                          color="success"
                          fontSize="1rem"
                          fontWeight="bold"
                          sx={{ width: "15%", textAlign: "right" }}
                        >
                          S/. {formatCurrency(deposito.monto)}
                        </VuiTypography>
                      </VuiBox>
                    ))}
                  </VuiBox>
                ) : (
                  <VuiBox
                    textAlign="center"
                    py={4}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.03)",
                      borderRadius: "12px",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                    }}
                  >
                    <VuiTypography color="text" fontSize="1rem">
                      {fechaInicio || fechaFin
                        ? "No se encontraron depósitos en el rango de fechas seleccionado."
                        : "No hay depósitos registrados."}
                    </VuiTypography>
                  </VuiBox>
                )}
              </VuiBox>
            </Card>
          </VuiBox>
        </VuiBox>
        <Footer />
      </VuiBox>
    </DashboardLayout>
  );
}

export default Dashboard;